import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/format";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/orders")
      .then(({ data }) => setOrders(data))
      .catch(() => {})
      .finally(() => setOrdersLoading(false));
  }, [user]);

  if (loading) return <main className="p-20 text-center opacity-60">Loading...</main>;
  if (!user) return <Navigate to="/login" replace state={{ from: "/account" }} />;

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
  };

  return (
    <main className="max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24" data-testid="account-page">
      <div className="flex justify-between items-end flex-wrap gap-6 fade-up">
        <div>
          <p className="tracking-overline text-[11px] uppercase opacity-60">Your Atelier</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-tight">
            Welcome, <span className="italic">{user.name?.split(" ")[0] || "—"}</span>.
          </h1>
          <p className="opacity-70 mt-3 text-sm" data-testid="account-email">{user.email}</p>
        </div>
        <button onClick={handleLogout} className="btn-ghost" data-testid="logout-button">Sign Out</button>
      </div>

      <section className="mt-20 fade-up-delay-1">
        <div className="flex items-center justify-between mb-10">
          <h2 className="font-display text-3xl">Your Orders</h2>
          <p className="text-[11px] uppercase tracking-[0.22em] opacity-60">{orders.length} {orders.length === 1 ? "order" : "orders"}</p>
        </div>

        {ordersLoading ? (
          <p className="opacity-60 text-sm">Loading orders…</p>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center border" style={{ borderColor: "var(--line)" }} data-testid="account-no-orders">
            <p className="font-display italic text-2xl">No orders yet.</p>
            <Link to="/shop" className="btn-outline mt-8 inline-flex">Begin Your Collection</Link>
          </div>
        ) : (
          <div className="space-y-6" data-testid="account-orders-list">
            {orders.map((order) => (
              <article
                key={order.id}
                className="border p-6 sm:p-8 grid grid-cols-1 md:grid-cols-12 gap-6"
                style={{ borderColor: "var(--line)" }}
                data-testid={`order-${order.id}`}
              >
                <div className="md:col-span-3">
                  <p className="tracking-overline text-[10px] uppercase opacity-60">Order</p>
                  <p className="font-bodoni text-sm mt-1">{order.id.slice(0, 12)}…</p>
                  <p className="text-xs opacity-60 mt-3">{new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div className="md:col-span-6">
                  <ul className="space-y-3">
                    {order.items.map((it, idx) => (
                      <li key={idx} className="flex gap-3 items-center text-sm">
                        <div className="w-12 h-16 overflow-hidden flex-shrink-0" style={{ background: "var(--line-soft)" }}>
                          {it.image && <img src={it.image} alt={it.name} className="object-cover w-full h-full" />}
                        </div>
                        <div>
                          <p className="font-display text-base">{it.name}</p>
                          <p className="text-[10px] uppercase tracking-[0.18em] opacity-60">Size {it.size} · Qty {it.quantity}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:col-span-3 md:text-right">
                  <p className="tracking-overline text-[10px] uppercase opacity-60">Total</p>
                  <p className="font-display text-2xl mt-1">{formatPrice(order.amount, order.currency)}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] mt-2" style={{ color: "var(--success, #4A6B53)" }}>
                    {order.payment_status === "paid" ? "✓ Paid" : order.payment_status}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
